import type { Allowance } from './types';

/**
 * Mirror of legacy manage.js `password_is_strong`. All must pass:
 * > 6 chars; contains a non-letter; not word-then-digits; not all digits;
 * not all the same character.
 */
export function passwordIsStrong(password: string): boolean {
  if (password.length <= 6) return false;
  if (!/[^A-Za-z]/.test(password)) return false;
  if (/^[A-Za-z][a-z]*\d+$/.test(password)) return false;
  if (/^\d+$/.test(password)) return false;
  if (/^(.)\1+$/.test(password)) return false;
  return true;
}

/** Legacy mailbox-username charset (manage.js). */
export const USERNAME_PATTERN = /^[.+a-zA-Z0-9_-]+$/;

/**
 * Epoch seconds → DD/MM/YYYY, read in UTC.
 *
 * `expiry_ts` is always midnight UTC: the backend derives it from a date-only
 * `expiry_date` (SQL DATE) via `Time::Piece->strptime('%Y-%m-%d')`, which parses
 * as GMT. Reading the UTC date components therefore reproduces the stored
 * calendar date exactly, independent of timezone. Matches legacy `list.tt`
 * (`[% Date.format(expiry_ts) %]`, which renders in server localtime) on the UK
 * servers, whose localtime is UTC or BST — both at or ahead of UTC, so midnight
 * UTC never rolls back to the previous day.
 */
export function formatExpiry(epochSeconds: number): string {
  const d = new Date(epochSeconds * 1000);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}

/** A mailbox is assigned once it has an Open-Xchange domain. */
export function isAssigned(a: Allowance): boolean {
  return a.openxchange_domain_id != null;
}

/** Product tier label shown in the table. */
export function productLabel(productType: string): 'Essential' | 'Pro' {
  return /essential/.test(productType) ? 'Essential' : 'Pro';
}

export type UpgradeState = 'none' | 'upgradeable' | 'pending' | 'bulk';

/**
 * Which upgrade affordance a row shows (mirrors list.tt + manage.js):
 * - none        → already Pro (upgrade_price null) → nothing
 * - pending     → renewal payment pending → hourglass tooltip, no action
 * - bulk        → part of a bulk purchase (price -1) → button opens the can't-upgrade modal
 * - upgradeable → button opens the £price confirmation
 */
export function upgradeState(a: Allowance): UpgradeState {
  if (a.upgrade_price == null) return 'none';
  if (Number(a.has_pending_payment) === 1) return 'pending';
  if (String(a.upgrade_price) === '-1') return 'bulk';
  return 'upgradeable';
}

/** Distinct domains from assigned mailboxes (mirrors manage.js `initFilter`). */
export function deriveDomains(allowances: Allowance[]): string[] {
  const domains: string[] = [];
  for (const a of allowances) {
    if (!isAssigned(a)) continue;
    const domain = a.full_login?.split('@')[1]?.trim();
    if (domain && !domains.includes(domain)) domains.push(domain);
  }
  return domains;
}
